import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import api from '../../services/api';

// ---------- helpers ----------

function formatarDataHora(valor) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (isNaN(d)) return valor;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ---------- mapa de tipos de log ----------

const MAPA_TIPO = {
  login: {
    icone: '🔐',
    corFundo: 'bg-blue-100',
    corIcone: 'text-blue-600',
    corBorda: 'border-blue-200',
    corLinha: 'bg-blue-400',
    label: 'Login',
  },
  alteracao: {
    icone: '✏️',
    corFundo: 'bg-amber-100',
    corIcone: 'text-amber-600',
    corBorda: 'border-amber-200',
    corLinha: 'bg-amber-400',
    label: 'Alteração',
  },
  upload: {
    icone: '📎',
    corFundo: 'bg-green-100',
    corIcone: 'text-green-600',
    corBorda: 'border-green-200',
    corLinha: 'bg-green-500',
    label: 'Upload de arquivo',
  },
  exclusaoAnexo: {
    icone: '🗑️',
    corFundo: 'bg-red-100',
    corIcone: 'text-red-600',
    corBorda: 'border-red-200',
    corLinha: 'bg-red-400',
    label: 'Exclusão de anexo',
  },
};

function obterConfigTipo(tipo) {
  return MAPA_TIPO[tipo] || {
    icone: '📋',
    corFundo: 'bg-gray-100',
    corIcone: 'text-gray-500',
    corBorda: 'border-gray-200',
    corLinha: 'bg-gray-400',
    label: tipo || 'Evento',
  };
}

// ---------- spinner ----------

function Spinner() {
  return (
    <div className="flex justify-center items-center py-16">
      <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );
}

// ---------- lista expansível ----------

function ListaExpansivel({ itens, limite = 3, rotulo }) {
  const [expandido, setExpandido] = useState(false);
  if (!itens || itens.length === 0) return null;
  const visiveis = expandido ? itens : itens.slice(0, limite);
  return (
    <div className="mt-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{rotulo}</p>
      <ul className="space-y-0.5">
        {visiveis.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
            <span className="text-gray-300 mt-0.5">•</span>
            <span>{typeof item === 'object' ? (item.campo || item.label || JSON.stringify(item)) : item}</span>
          </li>
        ))}
      </ul>
      {itens.length > limite && (
        <button
          onClick={() => setExpandido(prev => !prev)}
          className="text-xs text-brand-600 hover:text-brand-800 font-medium mt-1"
        >
          {expandido ? 'Ver menos' : `Ver mais ${itens.length - limite} item(s)`}
        </button>
      )}
    </div>
  );
}

// ---------- item de log na timeline ----------

function ItemLog({ log, ultimo }) {
  const cfg = obterConfigTipo(log.tipo);

  return (
    <div className="flex gap-4">
      {/* Linha vertical + ícone */}
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cfg.corFundo} border ${cfg.corBorda} text-base`}>
          {cfg.icone}
        </div>
        {!ultimo && (
          <div className="w-0.5 flex-1 mt-1 mb-0" style={{ background: 'linear-gradient(to bottom, #e5e7eb, transparent)', minHeight: 24 }} />
        )}
      </div>

      {/* Conteúdo */}
      <div className={`flex-1 bg-white border rounded-xl p-4 mb-4 ${cfg.corBorda}`}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.corFundo} ${cfg.corIcone}`}>
              {cfg.label}
            </span>
            {log.secao && (
              <span className="ml-2 text-xs text-gray-500">
                Seção: <span className="font-medium text-gray-700">{log.secao}</span>
              </span>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs font-medium text-gray-700">{formatarDataHora(log.dataHora || log.criadoEm)}</p>
            {log.ip && (
              <p className="text-xs text-gray-400 mt-0.5 font-mono">IP: {log.ip}</p>
            )}
          </div>
        </div>

        {/* Barra de progresso na hora do log */}
        {log.percentual !== undefined && log.percentual !== null && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500 shrink-0">Progresso:</span>
            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  log.percentual >= 80 ? 'bg-green-500' : log.percentual >= 40 ? 'bg-brand-600' : 'bg-amber-500'
                }`}
                style={{ width: `${Math.min(log.percentual, 100)}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-600 shrink-0">{log.percentual}%</span>
          </div>
        )}

        {/* Campos alterados */}
        {log.camposAlterados && log.camposAlterados.length > 0 && (
          <ListaExpansivel
            itens={log.camposAlterados}
            limite={3}
            rotulo="Campos alterados"
          />
        )}

        {/* Pendências */}
        {log.pendencias && log.pendencias.length > 0 && (
          <ListaExpansivel
            itens={log.pendencias}
            limite={3}
            rotulo="Pendências registradas"
          />
        )}

        {/* Mensagem livre */}
        {log.mensagem && (
          <p className="text-xs text-gray-600 mt-2 italic">{log.mensagem}</p>
        )}
      </div>
    </div>
  );
}

// ---------- página principal ----------

export default function HistoricoOnboarding() {
  const { id } = useParams();
  const location = useLocation();

  // Tenta pegar o nome da empresa pelo state de navegação (passado opcionalmente pelo link de origem)
  const [nomeEmpresa, setNomeEmpresa] = useState(
    location.state?.nomeEmpresa || location.state?.nomeFantasia || ''
  );
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    carregarHistorico();
  }, [id]);

  async function carregarHistorico() {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get(`/admin/onboardings/${id}/logs`);
      setLogs(data);

      // Se ainda não temos o nome da empresa, busca separado
      if (!nomeEmpresa) {
        try {
          const { data: onb } = await api.get(`/admin/onboardings/${id}`);
          setNomeEmpresa(onb.nomeFantasia || onb.razaoSocial || `Onboarding #${id}`);
        } catch {
          setNomeEmpresa(`Onboarding #${id}`);
        }
      }
    } catch {
      setErro('Erro ao carregar o histórico. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src="/logo_ht.png" alt="HeadThink" className="h-9 w-auto" />
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <p className="text-xs text-gray-400 leading-none">
                {nomeEmpresa || `Onboarding #${id}`}
              </p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">Histórico de Atividade</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/admin/onboarding/${id}`}
              className="btn-ghost text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Voltar ao onboarding
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {/* Legenda de tipos */}
        <div className="card mb-8 py-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Legenda</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(MAPA_TIPO).map(([chave, cfg]) => (
              <span
                key={chave}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${cfg.corFundo} ${cfg.corIcone} ${cfg.corBorda} font-medium`}
              >
                {cfg.icone} {cfg.label}
              </span>
            ))}
          </div>
        </div>

        {/* Conteúdo */}
        {loading ? (
          <Spinner />
        ) : erro ? (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="card text-center py-16">
            <div className="text-5xl mb-4">📋</div>
            <p className="font-medium text-gray-500">Nenhuma atividade registrada ainda</p>
            <p className="text-sm text-gray-400 mt-1">
              O histórico aparecerá aqui assim que o cliente acessar o portal.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-xs text-gray-400 mb-6 text-right">
              {logs.length} registro{logs.length !== 1 ? 's' : ''} encontrado{logs.length !== 1 ? 's' : ''}
            </p>
            <div>
              {logs.map((log, idx) => (
                <ItemLog
                  key={log.id || idx}
                  log={log}
                  ultimo={idx === logs.length - 1}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
